import happen from './happen';

type GridBreakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'xxl' | '3xl';

interface Breakpoints {
  readonly current: GridBreakpoint;
  min: (name: GridBreakpoint) => boolean;
  max: (name: GridBreakpoint) => boolean;
  between: (lower: GridBreakpoint, upper: GridBreakpoint) => boolean;
}

const gridBreakpoints: Record<GridBreakpoint, number> = {
  '3xl': 1600,
  xxl: 1400,
  xl: 1200,
  lg: 992,
  md: 768,
  sm: 576,
  xs: 0,
};

const breakpoints: Breakpoints = {
  get current() {
    const { innerWidth } = window;

    return Object.entries(gridBreakpoints).find(([, size]) => innerWidth >= size)![0] as GridBreakpoint;
  },
  min(name) {
    return happen(window.innerWidth).greaterThanOrEqual(gridBreakpoints[name]);
  },
  /**
   * Checks if the current window width is less than the specified breakpoint.
   * Note: This matches the SCSS media-breakpoint-max behavior which applies
   * styles up to but not including the breakpoint value by subtracting 0.02.
   *
   * Why subtract .02px?
   * Browsers don't currently support range context queries.
   * So we work around the limitations of min- and max- prefixes and viewports with fractional widths
   * (which can occur under certain conditions on high-dpi devices, for instance) by using values with higher precision.
   */
  max(name) {
    return happen(window.innerWidth).lessThanOrEqual(gridBreakpoints[name] - 0.02);
  },
  between(lower, upper) {
    return happen(window.innerWidth).between(gridBreakpoints[lower], gridBreakpoints[upper]);
  },
};

export type { GridBreakpoint };
export default breakpoints;
